import {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import socket from 'services/socket';
import { SOCKET_ACTIONS } from '@medicusunion/front-utils';
import { audioCallLocalStream } from 'services/voip';
import { AudioCallStatuses } from 'constants/voip';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectPatientAudioCallStatus } from 'store/call/selectors';
import { setAudioCallStatus } from 'store/call/slice';

import {
  StyledAudioCall,
  StyledAudioCallVideo,
  StyledFullScreen
} from './styled';
import { AudioCallContentMethods, AudioCallHeaderMethods } from './_types';
import MeetingAction from './MeetingAction';
import AudioRouteButton from './AudioRouteButton';
import CallHeader from './CallHeader';
import Draggable from './Draggable';
import MicrophoneButton from './MicrophoneButton';
import CameraButton from './CameraButton';
import { audioCallRemoteVideoRef } from './_refs';
import CallSound from './CallSound';

const AudioCall: ForwardRefRenderFunction<AudioCallContentMethods> = (
  _,
  ref
) => {
  const dispatch = useAppDispatch();

  const callHeaderRef = useRef<AudioCallHeaderMethods | null>(null);
  const selfVideoRef = useRef<HTMLVideoElement | null>(null);

  const status = useAppSelector(selectPatientAudioCallStatus);

  const [callerName, setCallerName] = useState('');
  const [isSelfVideoEnabled, setSelfVideoEnabled] = useState(false);
  const [isAudioEnabled, setAudioEnabled] = useState(true);
  const [remoteVideoEnabled, setRemoteVideoEnabled] = useState(false);
  const [isFullScreen, setFullScreen] = useState(true);

  useEffect(() => {
    if (
      isSelfVideoEnabled &&
      audioCallLocalStream.current &&
      selfVideoRef.current
    ) {
      selfVideoRef.current.srcObject = audioCallLocalStream.current;
    }
  }, [isSelfVideoEnabled]);

  const resetValues = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    audioCallLocalStream.current = null;

    dispatch(setAudioCallStatus());

    setCallerName('');
    setSelfVideoEnabled(false);
    setAudioEnabled(true);
    setRemoteVideoEnabled(false);
    setFullScreen(true);
  }, [dispatch]);

  const onEndCall = useCallback(() => {
    audioCallLocalStream.current?.getTracks().forEach(track => track.stop());

    if (socket.socket) {
      socket.sendToPeer(SOCKET_ACTIONS.LEAVE);
      socket.io.disconnect();
    }

    resetValues();
  }, [resetValues]);

  useImperativeHandle(ref, () => ({
    call(name) {
      dispatch(setAudioCallStatus(AudioCallStatuses.calling));
      setCallerName(name);
    },
    connecting() {
      dispatch(setAudioCallStatus(AudioCallStatuses.connecting));
    },
    connect() {
      dispatch(setAudioCallStatus(AudioCallStatuses.connected));
      callHeaderRef.current?.setStartTime(new Date());
    },
    end() {
      onEndCall();
    },
    setRemoteVideoEnabled
  }));

  const toggleFullScreen = useCallback(() => {
    setFullScreen(prev => !prev);
  }, []);

  const hasVideo = remoteVideoEnabled || isSelfVideoEnabled;

  if (typeof status === 'undefined') {
    return null;
  }

  return (
    <Draggable isFullScreen={isFullScreen}>
      <StyledAudioCall $hasVideo={hasVideo}>
        <StyledFullScreen>
          <MeetingAction
            icon={isFullScreen ? 'fullscreenExit' : 'fullscreen'}
            onClick={toggleFullScreen}
          />
        </StyledFullScreen>
        <div className="audio-call-content">
          <StyledAudioCallVideo>
            <video
              ref={audioCallRemoteVideoRef}
              autoPlay
              playsInline
              muted={false}
              controls={false}
            />
          </StyledAudioCallVideo>
          {isSelfVideoEnabled && (
            <StyledAudioCallVideo $small={remoteVideoEnabled}>
              <video
                ref={selfVideoRef}
                autoPlay
                playsInline
                muted
                controls={false}
              />
            </StyledAudioCallVideo>
          )}
          <CallHeader
            ref={callHeaderRef}
            callerName={callerName}
            status={status}
          />
        </div>
        <div className="audio-call-buttons">
          <CameraButton
            isEnabled={isSelfVideoEnabled}
            onChange={setSelfVideoEnabled}
            status={status}
          />
          <AudioRouteButton
            isVideoEnabled={isSelfVideoEnabled}
            isAudioEnabled={isAudioEnabled}
          />
          <MicrophoneButton
            isEnabled={isAudioEnabled}
            onChange={setAudioEnabled}
          />
          <MeetingAction isDecline icon="hang" onClick={onEndCall} />
        </div>
        <CallSound />
      </StyledAudioCall>
    </Draggable>
  );
};

export default forwardRef(AudioCall);
